import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateEscolaWebIntegrationAsync,
  useGetEscolaWebIntegrationByIdAsync,
  useUpdateEscolaWebIntegrationAsync,
} from '../../../services/IntegrationService/EscolaWebService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { EscolaWebIntegrationFormSchema } from '../../../services/IntegrationService/EscolaWebService/schemas';

interface EscolaWebEditionFormProps {
  escolaWebIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type EscolaWebEditionFormInput = zod.infer<
  typeof EscolaWebIntegrationFormSchema
>;

const EscolaWebEditionForm = ({
  escolaWebIdToUpdate,
  handleSaveForm,
}: EscolaWebEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<EscolaWebEditionFormInput>({
    resolver: zodResolver(EscolaWebIntegrationFormSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      apiTokenBase64: '',
      baseUrl: 'https://backend.escolaweb.com.br',
    },
  });

  const {
    mutate: mutateCreateEscolaWebIntegration,
    isLoading: isLoadingCreateEscolaWebIntegration,
  } = useCreateEscolaWebIntegrationAsync();
  const {
    mutate: mutateUpdateEscolaWebIntegration,
    isLoading: isLoadingUpdateEscolaWebIntegration,
  } = useUpdateEscolaWebIntegrationAsync();
  const {
    mutateAsync: mutateGetEscolaWebIntegrationByIdAsync,
    isLoading: isLoadingGetEscolaWebIntegrationById,
  } = useGetEscolaWebIntegrationByIdAsync();

  const handleSave = (data: EscolaWebEditionFormInput) => {
    if (escolaWebIdToUpdate) {
      mutateUpdateEscolaWebIntegration(
        { id: escolaWebIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração EscolaWeb atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateEscolaWebIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração EscolaWeb criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (escolaWebIdToUpdate) {
      mutateGetEscolaWebIntegrationByIdAsync(escolaWebIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [escolaWebIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Nome"
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Api Token (Base64)"
              />
            )}
            name={'apiTokenBase64'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Url"
              />
            )}
            name={'baseUrl'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Descrição"
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label="Ativo"
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateEscolaWebIntegration ||
              isLoadingGetEscolaWebIntegrationById ||
              isLoadingUpdateEscolaWebIntegration
            }
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default EscolaWebEditionForm;
